import React, { forwardRef, useEffect, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import { useConfigSlice } from "Routes/SetFan/Components/BuilderContext";
import styled from "styled-components";
import { hexToArrayRGB } from "Utils";

const Link = Quill.import("formats/link");

export const toolbarId = "quill-editor-toolbar";

export const modules = {
  toolbar: {
    container: `#${toolbarId}`,
  },
};

export const formats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "link",
];

interface Props {
  quill: ReactQuill | null;
}

export const EditorToolbar = forwardRef<HTMLDivElement, Props>(
  ({ quill }, ref) => {
    const [format, setFormat] = useState<Record<string, any> | null>(null);
    const { fields } = useConfigSlice(["buttonColor"]);
    const buttonColorRGB =
      fields.buttonColor.content && hexToArrayRGB(fields.buttonColor.content);
    const isButtonColorDark =
      buttonColorRGB &&
      0.2126 * buttonColorRGB[0] +
        0.7152 * buttonColorRGB[1] +
        0.0722 * buttonColorRGB[2] <
        128;

    const isHeadingSelected = format?.size === "large";
    const isHeadingDisabled =
      format?.bold || format?.italic || format?.underline;

    useEffect(() => {
      const editor = quill?.getEditor();
      const updateFormat = () => {
        const selection = editor?.getSelection();
        selection &&
          setFormat(editor?.getFormat(selection.index, selection.length));
      };
      editor?.on("selection-change", updateFormat);
      editor?.on("text-change", updateFormat);

      return () => {
        editor?.off("selection-change", updateFormat);
        editor?.off("text-change", updateFormat);
      };
    }, [quill]);

    useEffect(() => {
      class StyledLink extends Link {
        static create(value) {
          let node = super.create(value);
          node.setAttribute("href", this.sanitize(value));
          node.setAttribute("target", "_blank");
          node.setAttribute(
            "style",
            `--color: ${
              isButtonColorDark ? fields.buttonColor.content : "black"
            };`,
          );
          return node;
        }
      }

      Quill.register(StyledLink, true);
    }, [isButtonColorDark]);

    return (
      <Container
        ref={ref}
        id={toolbarId}
        data-is-heading-disabled={isHeadingDisabled}
      >
        <select className="ql-size" defaultValue="normal">
          <option value="large">Heading</option>
          <option value="normal">Body</option>
        </select>
        <button className="ql-bold" disabled={isHeadingSelected} />
        <button className="ql-italic" disabled={isHeadingSelected} />
        <button className="ql-underline" disabled={isHeadingSelected} />
        <button className="ql-link" />
      </Container>
    );
  },
);

const Container = styled.div`
  border-radius: 7px 7px 0 0;

  && {
    border-bottom: none;
  }

  button:disabled {
    pointer-events: none;

    path,
    line {
      stroke: var(--disabled-color) !important;
    }

    rect {
      fill: var(--disabled-color) !important;
    }
  }

  &[data-is-heading-disabled="true"] {
    span[data-value="large"] {
      color: var(--disabled-color) !important;
      pointer-events: none;
    }
  }
`;
